/**
 * Lógica pura de navegação/progresso dos Tutoriais — sem React, sem rede, para
 * ser testável em isolamento (protocolo de entrega sem revisão visual). Espelha
 * o que o AMA Flow faz no LessonView: achatar módulos→aulas, achar a aula
 * anterior/próxima atravessando módulos, e contar progresso. Trabalha sobre uma
 * forma mínima (só o que precisa), então serve tanto ao tipo do currículo do
 * back quanto a mocks de teste.
 */

export interface AulaLite {
  id: string;
}
export interface ModuloLite {
  id: string;
  aulas: AulaLite[];
}

/** Achata os módulos numa lista ordenada de aulas, guardando o módulo de cada. */
export function achatarAulas<M extends ModuloLite>(
  modulos: M[],
): Array<{ aulaId: string; moduloId: string }> {
  return modulos.flatMap((m) => m.aulas.map((a) => ({ aulaId: a.id, moduloId: m.id })));
}

/** Aula anterior e próxima (ids), atravessando a fronteira de módulos. null nas pontas. */
export function vizinhas(
  modulos: ModuloLite[],
  aulaId: string,
): { anterior: string | null; proxima: string | null } {
  const flat = achatarAulas(modulos);
  const i = flat.findIndex((f) => f.aulaId === aulaId);
  if (i === -1) return { anterior: null, proxima: null };
  return {
    anterior: i > 0 ? flat[i - 1]!.aulaId : null,
    proxima: i < flat.length - 1 ? flat[i + 1]!.aulaId : null,
  };
}

/** Percentual concluído (0..100), arredondado. Denominador = total de aulas. */
export function progressoPct(totalAulas: number, concluidas: number): number {
  if (totalAulas <= 0) return 0;
  return Math.round((concluidas / totalAulas) * 100);
}

/** Contagem de aulas concluídas de um módulo (ex.: "1/2"). */
export function progressoModulo(
  modulo: ModuloLite,
  concluidas: Set<string>,
): { feitas: number; total: number } {
  const total = modulo.aulas.length;
  const feitas = modulo.aulas.filter((a) => concluidas.has(a.id)).length;
  return { feitas, total };
}

/** Primeira aula do currículo (para seleção inicial). null se vazio. */
export function primeiraAula(modulos: ModuloLite[]): string | null {
  return achatarAulas(modulos)[0]?.aulaId ?? null;
}
